import { useMemo, useState } from "react";
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import { Collapse, Input, Segmented, Space, Tag, Typography } from "antd";
import { SlicerTokenInput } from "../shared/SlicerTokenInput";
import type { ConfiguratorSharedProps } from "../../widgetDefinition";
import type {
  ChartCustomRefLine,
  ScatterplotComponentSpec,
  ScatterplotNonSeriesResponse,
  ScatterplotPoint,
  ScatterplotTrendline,
  ScatterplotEntityGroupBy,
  ScatterplotTrendlineScope,
  ScatterplotXAxisMode,
} from "../../Processing/types";
import { buildScatterplotOptions, selectionAggWord } from "../../WidgetComponents/buildScatterplotOptions";
import { STYLE_PRESETS } from "../../Processing/presets";
import { aggOptionsFor, allowedAggsFromDropdown, coerceAgg } from "../shared/metricAggregations";
import { useReduxMetricDropdown } from "../shared/useReduxMetricDropdown";
import { MetricSelect } from "../shared/MetricSelect";
import { ConfigPanelLayout } from "../shared/ConfigPanelLayout";
import { PreviewShell } from "../shared/PreviewShell";
import { PreviewDisclaimer } from "../shared/PreviewDisclaimer";
import { DateRangeForm } from "../shared/DateRangeForm";
import { PlayersForm, resolveSubjectNames } from "../shared/PlayersForm";
import { ToggleOption } from "../shared/ToggleOption";
import { RefLineList, RefLineModal } from "../shared/RefLineEditor";
import { useConfiguratorSync } from "../shared/useConfiguratorSync";
import { resolveDateRange } from "../../Utils/resolveDateRange";
import type {
  ScatterplotAxisFormState,
  ScatterplotAxisKey,
  ScatterplotDotGrouping,
  ScatterplotFormState,
} from "./scatterplotState";
import {
  scatterplotReducer,
  createInitialState,
  hydrateFromSpec,
  formStateToSpec,
  specBlockReason,
} from "./scatterplotState";

const { Panel } = Collapse;
const { Text } = Typography;

const DEFAULT_PLOT_LINE_COLOR = "#000000";

const DOT_GROUPING_OPTIONS: { value: ScatterplotDotGrouping; label: string }[] = [
  { value: "player", label: "Player" },
  { value: "position", label: "Position" },
  { value: "drill", label: "Drill" },
  { value: "team", label: "Team" },
];

// ── Mock preview data ──
// The preview feeds the REAL options builder with a synthetic backend response, so
// what the modal shows is exactly what the grid widget will render.

const MOCK_ENTITIES: Record<ScatterplotEntityGroupBy, string[]> = {
  player: ["J. Doe", "A. Lee", "S. Kim"],
  position: ["Forward", "Midfield", "Back"],
  drill: ["Drill A", "Drill B", "Drill C"],
};

const MOCK_GAMEDAYS: Array<{ key: string; label: string }> = [
  { key: "gd", label: "GD" },
  { key: "gd+1", label: "GD+1" },
  { key: "gd-1", label: "GD-1" },
  { key: "practice", label: "Practice" },
];

const POINTS_PER_SERIES = 9;
const DAY_MS = 86_400_000;

/** Deterministic integer noise in [-n, n]. */
const noise = (seed: number, n: number) => ((seed * 37) % (2 * n + 1)) - n;

function mockFit(points: ScatterplotPoint[], seriesKey: string): ScatterplotTrendline | null {
  const n = points.length;
  if (n < 2) return null;
  const meanX = points.reduce((a, p) => a + p.x, 0) / n;
  const meanY = points.reduce((a, p) => a + p.y, 0) / n;
  const sxx = points.reduce((a, p) => a + (p.x - meanX) ** 2, 0);
  if (sxx === 0) return null;
  const sxy = points.reduce((a, p) => a + (p.x - meanX) * (p.y - meanY), 0);
  const syy = points.reduce((a, p) => a + (p.y - meanY) ** 2, 0);
  const slope = sxy / sxx;
  const intercept = meanY - slope * meanX;
  const r = syy > 0 ? sxy / Math.sqrt(sxx * syy) : 0;
  const xs = points.map((p) => p.x);
  const x0 = Math.min(...xs);
  const x1 = Math.max(...xs);
  return {
    series_key: seriesKey, slope, intercept,
    x0, y0: slope * x0 + intercept, x1, y1: slope * x1 + intercept,
    r: Math.round(r * 1e4) / 1e4, r_squared: Math.round(r * r * 1e4) / 1e4, n,
  };
}

/** Selected player names when the selection holds only players, so a filtered preview
 *  shows those players; otherwise placeholder names. */
function previewEntities(state: ScatterplotFormState, selectedNames: string[]): string[] {
  const onlyPlayers =
    state.selectedSubjects.length > 0 && state.selectedSubjects.every((s) => s.startsWith("player-"));
  return state.groupBy === "player" && onlyPlayers
    ? selectedNames.slice(0, state.selectedSubjects.length)
    : MOCK_ENTITIES[state.groupBy];
}

function buildMockResponse(state: ScatterplotFormState, entities: string[]): ScatterplotNonSeriesResponse {
  const dateMode = state.xAxisMode === "date";
  const palette = STYLE_PRESETS.default.colors;
  const today = Date.now() - (Date.now() % DAY_MS);

  // The backend labels the one-dot-per-day series "Team Average"; the options builder renames it.
  const series = state.groupByGameDate
    ? MOCK_GAMEDAYS.map((g) => ({ key: g.key, label: g.label, gameday: g.key }))
    : state.teamAverages
      ? [{ key: "team", label: "Team Average", gameday: null }]
      : entities.map((name, i) => ({
          key: `${state.groupBy}-${i + 1}`, label: name, gameday: null,
        }));

  const points: ScatterplotPoint[] = [];
  series.forEach((s, si) => {
    for (let i = 0; i < POINTS_PER_SERIES; i++) {
      const seed = si * POINTS_PER_SERIES + i;
      const dayIndex = seed % 28;
      const date = new Date(today - dayIndex * DAY_MS).toISOString().slice(0, 10);
      const metricX = 200 + i * 40 + si * 25 + noise(seed, 30);
      const x = dateMode ? today - dayIndex * DAY_MS : metricX;
      const y = Math.round((2 + 0.012 * metricX + si * 0.6 + noise(seed + 7, 12) / 10) * 10) / 10;
      points.push({
        x, y, date,
        entity_key: state.teamAverages ? "team" : `${state.groupBy}-${(seed % entities.length) + 1}`,
        entity_label: state.teamAverages
          ? "Team Average"
          : entities[(state.groupByGameDate ? seed : si) % entities.length],
        series_key: s.key,
        series_label: s.label,
        hex: palette[si % palette.length],
        x_label: dateMode ? date : String(Math.round(x)),
        y_label: String(y),
        position: state.groupBy === "position" && !state.teamAverages ? s.label : null,
        gameday: s.gameday,
      });
    }
  });

  const trendlines: ScatterplotTrendline[] = [];
  if (state.showTrendline) {
    if (state.trendlineScope === "series") {
      for (const s of series) {
        const fit = mockFit(points.filter((p) => p.series_key === s.key), s.key);
        if (fit) trendlines.push(fit);
      }
    } else {
      const fit = mockFit(points, "__all__");
      if (fit) trendlines.push(fit);
    }
  }

  return {
    type: "scatterplot",
    x_axis: {
      mode: dateMode ? "date" : "metric",
      label: dateMode
        ? "Date"
        : state.xAxis.displayNameOverride || state.xAxis.displayName || "X Axis",
      decimals: dateMode ? 0 : 1,
      low_good: false,
      plot_lines: dateMode ? [] : state.xAxis.plotLines,
    },
    y_axis: {
      mode: "metric",
      label: state.yAxis.displayNameOverride || state.yAxis.displayName || "Y Axis",
      decimals: 1,
      low_good: false,
      plot_lines: state.yAxis.plotLines,
    },
    points,
    trendlines,
    dropped_unpaired: 0,
  };
}

/** Minimal spec for the preview options builder — placeholder-safe while the form
 *  is incomplete (formStateToSpec returns null until it validates). */
function buildPreviewSpec(state: ScatterplotFormState, selectedNames: string[]): ScatterplotComponentSpec {
  const axis = (a: ScatterplotAxisFormState, fallback: string) => ({
    queryId: a.queryId,
    displayName: a.displayName || fallback,
    metricIdentifierType: "metric" as const,
    metricAgg: a.metricAgg,
    plotLines: a.plotLines,
    axis_builder_misc: { selectedMetricId: a.metricId },
  });
  return {
    type: "scatterplot",
    display: { title: state.title || "", showLegend: state.showLegend },
    xAxisMode: state.xAxisMode,
    xAxis: state.xAxisMode === "date" ? null : axis(state.xAxis, "X Axis"),
    yAxis: axis(state.yAxis, "Y Axis"),
    widgetMutations: {
      ...(state.teamAverages && { teamAverages: true }),
      ...(state.groupByGameDate && { groupByGameDate: true }),
      ...(state.showTrendline && { showTrendline: true, trendlineScope: state.trendlineScope }),
      ...(state.showPointLabels && { showPointLabels: true }),
      ...(state.showPointLabels && state.showPointDates && { showPointDates: true }),
    },
    scatterplot_builder_misc: {
      selectedSubjects: state.selectedSubjects,
      selectedSubjectNames: selectedNames,
    },
  };
}

// ── Axis form (one panel body per axis) ──

interface AxisFormProps {
  axis: ScatterplotAxisFormState;
  metricDropdown: any[];
  onChange: (patch: Partial<Omit<ScatterplotAxisFormState, "queryId">>) => void;
}

function metricLabelFromDropdown(metricDropdown: any[], value: string): string {
  for (const group of metricDropdown) {
    const opt = group.options?.find((o: any) => o.value === value);
    if (opt) return opt.label ?? opt.displayLabel ?? value;
  }
  return value;
}

function AxisForm({ axis, metricDropdown, onChange }: AxisFormProps) {
  const [refLineModalOpen, setRefLineModalOpen] = useState(false);
  const [editingIndex, setEditingIndex] = useState<number | null>(null);

  const saveRefLine = (line: ChartCustomRefLine) => {
    const next = editingIndex !== null
      ? axis.plotLines.map((rl, i) => (i === editingIndex ? line : rl))
      : [...axis.plotLines, line];
    onChange({ plotLines: next });
    setRefLineModalOpen(false);
  };

  return (
    <Space direction="vertical" size={8} style={{ width: "100%" }}>
      <MetricSelect
        metricDropdown={metricDropdown}
        value={axis.metricId}
        onChange={(v) =>
          onChange({
            metricId: v,
            displayName: metricLabelFromDropdown(metricDropdown, v),
            // A helmet count has no best: the backend rejects it on save.
            metricAgg: coerceAgg(axis.metricAgg, allowedAggsFromDropdown(metricDropdown, v)),
          })
        }
      />
      {axis.metricId != null && (
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <Text style={{ fontSize: 12 }}>Aggregation</Text>
          <Segmented
            size="small"
            options={aggOptionsFor(allowedAggsFromDropdown(metricDropdown, axis.metricId))}
            value={axis.metricAgg}
            onChange={(v) => onChange({ metricAgg: v as "avg" | "sum" | "best" })}
          />
        </div>
      )}
      <Space size={8} align="center" style={{ width: "100%" }}>
        <Text style={{ fontSize: 11, color: "#888", minWidth: 60 }}>Axis label</Text>
        <Input
          size="small"
          placeholder={axis.displayName || "Auto"}
          value={axis.displayNameOverride}
          onChange={(e) => onChange({ displayNameOverride: e.target.value })}
          style={{ flex: 1 }}
        />
      </Space>
      <div style={{ width: "100%" }}>
        <Space size={8} align="center">
          <Text style={{ fontSize: 11, color: "#888", minWidth: 60 }}>Plot lines</Text>
          <Tag
            style={{ cursor: "pointer", borderStyle: "dashed" }}
            onClick={() => {
              setEditingIndex(null);
              setRefLineModalOpen(true);
            }}
          >
            + Add line
          </Tag>
        </Space>
        <RefLineList
          lines={axis.plotLines}
          defaultColor={DEFAULT_PLOT_LINE_COLOR}
          onEdit={(i) => {
            setEditingIndex(i);
            setRefLineModalOpen(true);
          }}
          onRemove={(i) => onChange({ plotLines: axis.plotLines.filter((_, idx) => idx !== i) })}
        />
        <RefLineModal
          open={refLineModalOpen}
          initial={editingIndex !== null ? axis.plotLines[editingIndex] : null}
          defaultColor={DEFAULT_PLOT_LINE_COLOR}
          onSave={saveRefLine}
          onCancel={() => setRefLineModalOpen(false)}
        />
      </div>
    </Space>
  );
}

// ── Main configurator ──

export function ScatterplotConfigurator({
  onSpecChange,
  existingSpec,
  metrics,
  getSubjectEntities,
}: ConfiguratorSharedProps<ScatterplotComponentSpec>) {
  const [state, dispatch] = useConfiguratorSync({
    reducer: scatterplotReducer,
    existingSpec,
    hydrate: hydrateFromSpec,
    createInitial: createInitialState,
    toSpec: formStateToSpec,
    onSpecChange,
    metrics,
    getSubjectEntities,
  });

  const { metricDropdown } = useReduxMetricDropdown({ includeHelmet: true });

  const selectedNames = useMemo(
    () => resolveSubjectNames(state.selectedSubjects, [], getSubjectEntities),
    [state.selectedSubjects, getSubjectEntities],
  );

  const previewOptions = useMemo(() => {
    const spec = buildPreviewSpec(state, selectedNames);
    return buildScatterplotOptions(spec, buildMockResponse(state, previewEntities(state, selectedNames)));
  }, [state, selectedNames]);

  const blockReason = specBlockReason(state);
  const selectionAggLabel = selectionAggWord(
    state.xAxisMode === "date" ? null : state.xAxis.metricAgg,
    state.yAxis.metricAgg,
  );
  const dateLabel = resolveDateRange(state);
  const isFullTeam = state.selectedSubjects.length === 0 || state.selectedSubjects.includes("full-team");

  const axisSummary = (axis: ScatterplotAxisFormState) =>
    axis.metricId ? `${axis.displayNameOverride || axis.displayName} · ${axis.metricAgg}` : null;

  const onAxisChange = (key: ScatterplotAxisKey) =>
    (patch: Partial<Omit<ScatterplotAxisFormState, "queryId">>) =>
      dispatch({ type: "UPDATE_AXIS", axis: key, patch });

  const form = (
    <>
      <div style={{ padding: "12px 16px", borderBottom: "1px solid #e8ecf0" }}>
        <Space size={8} align="center" style={{ width: "100%" }}>
          <Text style={{ fontSize: 12, minWidth: 40 }}>Title</Text>
          <SlicerTokenInput
            size="small"
            placeholder="e.g. Load vs Sleep Quality"
            value={state.title}
            onChange={(title) => dispatch({ type: "SET_TITLE", title })}
            style={{ flex: 1 }}
          />
        </Space>
      </div>

      <Collapse
        accordion
        activeKey={state.expandedKey}
        onChange={(key) => dispatch({ type: "SET_EXPANDED_KEY", key })}
        style={{ borderRadius: 0, borderTop: "none" }}
      >
        <Panel
          key="__date_range"
          header={
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <span className="awm-step-number">1</span>
              <Text strong style={{ fontSize: 13 }}>Date Range</Text>
              <Text type="secondary" style={{ fontSize: 11 }}>{dateLabel}</Text>
            </div>
          }
        >
          <DateRangeForm
            value={state}
            onChange={(patch) => dispatch({ type: "UPDATE_DATE_RANGE", patch })}
          />
        </Panel>

        <Panel
          key="__players"
          header={
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <span className="awm-step-number">2</span>
              <Text strong style={{ fontSize: 13 }}>Players</Text>
              <Text type="secondary" style={{ fontSize: 11 }}>
                {isFullTeam ? "Full Team" : `${state.selectedSubjects.length} selected`}
              </Text>
            </div>
          }
        >
          <PlayersForm
            value={state}
            onChange={(patch) => dispatch({ type: "UPDATE_PLAYERS", patch })}
            getSubjectEntities={getSubjectEntities}
            hideDrills
          />
          <div style={{ marginTop: 8 }}>
            <ToggleOption
              label="Exclude Injured Players"
              description="Hide athletes with an active injury from this scatterplot."
              checked={state.excludeInjuredPlayers}
              onChange={(v) => dispatch({ type: "SET_EXCLUDE_INJURED_PLAYERS", value: v })}
            />
          </div>
        </Panel>

        <Panel
          key="__x_axis"
          header={
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <span className="awm-step-number">3</span>
              <Text strong style={{ fontSize: 13 }}>X Axis</Text>
              <Text type="secondary" style={{ fontSize: 11 }}>
                {state.xAxisMode === "date" ? "Date" : axisSummary(state.xAxis)}
              </Text>
            </div>
          }
        >
          <div className="awm-step-body">
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
              <Text style={{ fontSize: 12 }}>Mode</Text>
              <Segmented
                size="small"
                options={[
                  { label: "Metric", value: "metric" },
                  { label: "Date", value: "date" },
                ]}
                value={state.xAxisMode}
                onChange={(v) => dispatch({ type: "SET_X_AXIS_MODE", value: v as ScatterplotXAxisMode })}
              />
            </div>
            {state.xAxisMode === "date" ? (
              <Text type="secondary" style={{ fontSize: 11 }}>
                Each dot plots its calendar day on the X axis against the Y metric.
              </Text>
            ) : (
              <AxisForm
                axis={state.xAxis}
                metricDropdown={metricDropdown}
                onChange={onAxisChange("xAxis")}
              />
            )}
          </div>
        </Panel>

        <Panel
          key="__y_axis"
          header={
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <span className="awm-step-number">4</span>
              <Text strong style={{ fontSize: 13 }}>Y Axis</Text>
              <Text type="secondary" style={{ fontSize: 11 }}>{axisSummary(state.yAxis)}</Text>
            </div>
          }
        >
          <div className="awm-step-body">
            <AxisForm
              axis={state.yAxis}
              metricDropdown={metricDropdown}
              onChange={onAxisChange("yAxis")}
            />
          </div>
        </Panel>
      </Collapse>

      <div style={{ padding: "12px 16px", borderTop: "1px solid #e8ecf0", display: "flex", flexDirection: "column", gap: 8 }}>
        <ToggleOption
          label="Show Legend"
          description="List each series above the chart."
          checked={state.showLegend}
          onChange={(v) => dispatch({ type: "SET_SHOW_LEGEND", value: v })}
        />
        <ToggleOption
          label="Group by Game Date"
          description="Color dots by their distance to a game (GD, GD+1, GD−1, …) instead of by entity."
          checked={state.groupByGameDate}
          onChange={(v) => dispatch({ type: "SET_GROUP_BY_GAME_DATE", value: v })}
        />
        <ToggleOption
          label="Show Trendline"
          description="Fit a least-squares line and report its R²."
          checked={state.showTrendline}
          onChange={(v) => dispatch({ type: "SET_SHOW_TRENDLINE", value: v })}
        />
        {state.showTrendline && (
          <div style={{ display: "flex", alignItems: "center", gap: 8, paddingLeft: 10 }}>
            <Text style={{ fontSize: 12 }}>Trendline scope</Text>
            <Segmented
              size="small"
              options={[
                { label: "All points", value: "all" },
                { label: "Per series", value: "series" },
              ]}
              value={state.trendlineScope}
              onChange={(v) => dispatch({ type: "SET_TRENDLINE_SCOPE", value: v as ScatterplotTrendlineScope })}
            />
          </div>
        )}
        <ToggleOption
          label="Show Point Labels"
          description="Label each dot with its player, position, or drill name."
          checked={state.showPointLabels}
          onChange={(v) => dispatch({ type: "SET_SHOW_POINT_LABELS", value: v })}
        />
        {state.showPointLabels && (
          <ToggleOption
            label="Show Point Dates"
            description="Add the date to each point label, for example J. Doe (Oct 19)."
            checked={state.showPointDates}
            onChange={(v) => dispatch({ type: "SET_SHOW_POINT_DATES", value: v })}
          />
        )}
      </div>
    </>
  );

  const preview = (
    <>
      <PreviewShell
        title={state.title || "Scatterplot Preview"}
        titleAddon={<PreviewDisclaimer />}
        hasContent={true}
        summaryText={blockReason ?? undefined}
      >
        <HighchartsReact
          highcharts={Highcharts}
          options={previewOptions}
          containerProps={{ style: { width: "100%", height: "100%", minHeight: 320 } }}
        />
      </PreviewShell>

      <div
        className="awm-groupby-bar"
        style={{ flexDirection: "row", alignItems: "center", flexWrap: "wrap", justifyContent: "flex-start", gap: 10 }}
      >
        <Text type="secondary" style={{ fontSize: 11 }}>One dot per</Text>
        <Segmented
          size="small"
          options={DOT_GROUPING_OPTIONS.map((o) => ({ label: o.label, value: o.value }))}
          value={state.teamAverages ? "team" : state.groupBy}
          onChange={(v) => dispatch({ type: "SET_DOT_GROUPING", value: v as ScatterplotDotGrouping })}
        />
        <Text type="secondary" style={{ fontSize: 11 }}>
          {state.teamAverages ? `per day (${selectionAggLabel} across the selection)` : "per day"}
        </Text>
      </div>
    </>
  );

  return <ConfigPanelLayout form={form} preview={preview} />;
}
